// Date utilities — 繁體中文 formatting + feel detection for P10 DateLabel.

import {
  format,
  formatDistanceToNow,
  isSameDay,
  differenceInCalendarDays,
  parseISO,
  subDays,
} from "date-fns";

/**
 * 繁體中文-formatted absolute date (e.g. `2026年9月20日`).
 * Used by DateLabel `feel="absolute"` + diary detail hero.
 */
export function formatAbsoluteZh(date: Date): string {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();
  return `${y}年${m}月${d}日`;
}

/**
 * Letter-style date (e.g. `九月廿日`, no digits).
 * Used optionally by Dashboard PageHero — drop cap feel.
 */
export function formatLetterZh(date: Date): string {
  const m =
    ["一", "二", "三", "四", "五", "六", "七", "八", "九", "十", "十一", "十二"][date.getMonth()] ??
    "";
  const d = date.getDate();
  const tens = ["", "十", "廿", "卅"][Math.min(3, Math.floor(d / 10))] ?? "";
  const ones = ["", "一", "二", "三", "四", "五", "六", "七", "八", "九"][d % 10] ?? "";
  return `${m}月${d <= 10 ? ones : `${tens}${d % 10 === 0 ? "" : ones}`}`;
}

/**
 * DateLabel feel detection — 5 buckets per component-api.md §2 P10.
 */
export type DateFeel = "today" | "yesterday" | "days-ago" | "days-until" | "absolute";

export function detectFeel(target: Date, now: Date = new Date()): DateFeel {
  // date-fns v4 `isToday` / `isYesterday` only honour `options.in` (the
  // constructor for timezone-aware date types) — they IGNORE `options.now`
  // and always compare against `Date.now()` (wall clock). The tests + any
  // user-supplied reference "now" therefore can't ride on those helpers.
  // Use `isSameDay` directly so the supplied `now` is the comparison anchor
  // (Bug W35.5-pre-1: DateLabel.test.tsx failed because the test's `now`
  // wasn't Sept 27 wall-clock).
  if (isSameDay(target, now)) return "today";
  if (isSameDay(target, subDays(now, 1))) return "yesterday";
  const diff = differenceInCalendarDays(target, now);
  if (diff < 0) return "days-ago";
  if (diff > 0) return "days-until";
  return "absolute";
}

export function formatRelative(target: Date, now: Date = new Date()): string {
  const feel = detectFeel(target, now);
  if (feel === "today") return "今天";
  if (feel === "yesterday") return "昨天";
  // Use calendar-day diff for the count (matches detectFeel), not the
  // 24h-rolling diffInDays which under-counts when the target was created
  // earlier in the day (e.g. diary written yesterday 10am viewed today 4am
  // would otherwise read "1 天前" instead of "2 天前").
  const diff = differenceInCalendarDays(target, now);
  if (diff < 0) return `${Math.abs(diff)} 天前`;
  if (diff > 0) return `${diff} 天後`;
  return formatAbsoluteZh(target);
}

/** Lightweight ISO formatter for machine contexts. */
export function formatIso(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/** Distance-to-now (for cards / activity feeds). */
export function formatDistance(date: Date, _now?: Date): string {
  return formatDistanceToNow(date, { addSuffix: true, locale: undefined });
}

/**
 * Parse a **date-only** value from this project's API into a local-midnight
 * `Date`.
 *
 * ES2015 parses `"2027-01-01"` as **UTC midnight**; `parseISO` parses it as
 * **local midnight**. Rendered with the local getters — which every formatter
 * here uses — the two differ by a day for anyone west of UTC. Measured in
 * `America/New_York`:
 *
 * ```
 * new Date("2027-01-01")  ->  Thu Dec 31 2026 19:00  ->  2026年12月31日
 * parseISO("2027-01-01")  ->  Fri Jan 01 2027 00:00  ->  2027年1月1日
 * ```
 *
 * The convention this app already follows everywhere else is **device-local
 * calendar days** for date-only values, and the *pair's* day (not the reader's)
 * for "today" — see `routes/Dashboard.tsx`, which deliberately parses
 * `` `${data?.date}T00:00:00` `` so a partner in another timezone sees the
 * pair's today. So "local midnight" is the intended reading; only the four
 * call sites that used a bare `new Date(iso)` were off by one.
 *
 * Note this is the opposite convention from {@link toApiDate}, which handles
 * **timestamps** and must add `Z`. A `YYYY-MM-DD` has no instant to recover; a
 * `YYYY-MM-DDTHH:MM:SS` does.
 */
export function parseDateOnly(iso: string): Date {
  return parseISO(iso.trim());
}

/**
 * Is this `YYYY-MM-DD` on or before today — as a **calendar** question.
 *
 * The bug this replaces compared a date-only string to a wall-clock instant:
 * `new Date(iso).getTime() > Date.now()`. `new Date("2026-10-08")` is
 * `2026-10-08T00:00:00Z`, so for a Hong Kong user between 00:00 and 08:00 on
 * 2026-10-08 the wall clock is still `2026-10-07T16:00Z`–`T23:59Z`, the chosen
 * date compares as later, and picking **today** is refused with
 * 「日期不可晚於今天」. Measured across the day, HKT:
 *
 * ```
 * 00:00 07:00 08:00 12:00 20:00 HKT  (picking 2026-10-08)
 * reject reject accept accept accept
 * ```
 *
 * Eight hours a day, on the first screen of pairing a new user, in the app's
 * home market. The server would have accepted it — `PairsRelationshipStart` is
 * a `date` column and the backend compares against `today_hkt()` — so the
 * client was refusing something the API allows.
 *
 * `differenceInCalendarDays` compares local calendar days, which is the
 * question the copy 「日期不可晚於今天」 is actually asking.
 */
export function isOnOrBeforeToday(iso: string, now: Date = new Date()): boolean {
  const parsed = parseDateOnly(iso);
  if (Number.isNaN(parsed.getTime())) return false;
  return differenceInCalendarDays(parsed, now) <= 0;
}

/**
 * Read a timestamp that came from this project's own API.
 *
 * The API serialises every datetime as **naive UTC** — `2026-10-02T03:53:42.986451`,
 * with no `Z` and no offset — because the backend stores `DateTime(timezone=True)`
 * in SQLite, which has no timezone type and strips it on read. Per ES2015,
 * `new Date()` parses a string with no zone designator as **local** time.
 *
 * So in any non-UTC locale every relative timestamp in the product is wrong by
 * the UTC offset. Measured in HKT (UTC+8) on a real page: five chat messages
 * posted seconds apart all read 「8 小時前」. Worse, a timestamp that lands in
 * the *future* yields a negative `diffSec`, which satisfies `< 60` and renders
 * as 「剛剛」 — an error in the wrong direction that reads as correct.
 *
 * Every test in the repo missed this because the author of a test writes the
 * mock string with the `Z` a real response does not have. So the ambiguity is
 * resolved in exactly one place, and the assertion in `date.test.ts` pins the
 * conversion itself rather than a rendered string, which makes it hold
 * regardless of the timezone the test runner happens to be in.
 */
export function toApiDate(iso: string): Date {
  const trimmed = iso.trim();
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  return new Date(hasZone ? trimmed : `${trimmed}Z`);
}

/**
 * Feed-style relative time (剛剛 / N 分鐘前 / N 小時前 / N 天前).
 *
 * `formatRelative` above is calendar-day granular, which is right for a diary
 * dated "今天" and wrong for a notification list: a row from two minutes ago
 * and one from six hours ago both read "今天", so the list cannot be ordered or
 * triaged by eye.
 *
 * This lives here rather than being written a third time — `WishChatPanel` and
 * `WishEventTimeline` each carry their own private copy of exactly this logic
 * (promoting those is a separate cleanup, not this change's business).
 *
 * Returns `""` for an unparseable input rather than "NaN 分鐘前": a missing
 * timestamp must not become visible garbage in a list row.
 */
export function formatFeedTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return "";
  const ms = toApiDate(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const diffSec = Math.round((now.getTime() - ms) / 1000);
  if (diffSec < 60) return "剛剛";
  if (diffSec < 3600) return `${Math.round(diffSec / 60)} 分鐘前`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)} 小時前`;
  return `${Math.round(diffSec / 86400)} 天前`;
}

/**
 * The calendar day a dashboard module row should show.
 *
 * **This exists because the backend sends two different shapes under one field.**
 * `diary` / `anniversary` / `calendar` / `daily_message` build `at` themselves as
 * `_date.isoformat()` → `2025-07-21`. `album` and `wishlist` pass their owning
 * module's timestamp straight through → `2025-07-25T16:36:48.606278`. The client
 * rendered `at` raw, so the same list showed three tidy dates above two rows of
 * raw ISO with microseconds — a wire format printed as if it were a label.
 *
 * The contract is not changed: `at` stays `str | None` and stays "whatever day
 * this module considers latest". Only the **reading** is normalised, because the
 * alternative — making the backend rewrite four modules' payloads — is a contract
 * change for a rendering problem, and this field's format was never the promise.
 *
 * Date-only values pass through **unchanged**. That is not laziness: a `YYYY-MM-DD`
 * has no instant to recover, and re-formatting it through `toApiDate` would
 * append a `Z` and shift it a day west of UTC. The three helpers here each own a
 * different shape and mixing them up is the bug this function exists to stop.
 */
export function formatActivityDate(iso: string | null | undefined): string {
  const trimmed = iso?.trim();
  if (!trimmed) return "";
  // Already a calendar day — nothing to convert, and converting would move it.
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const ms = toApiDate(trimmed).getTime();
  if (Number.isNaN(ms)) return "";
  return formatIso(new Date(ms));
}
